// frontend/src/components/Events/EditSectionModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { sectionsAPI } from '../../services/eventsAPI';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': { padding: theme.spacing(2) },
  '& .MuiDialog-paper': {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    minWidth: 0,
    height: 'min(82vh, 760px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.grey[100], 0.50)} 58%, ${alpha(theme.palette.primary.main, 0.055)}), radial-gradient(circle at 92% 8%, ${alpha(theme.palette.primary.main, 0.11)}, transparent 34%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.62),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.6),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.60)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
}));

const GlassSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2.8),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.common.white, 0.20)})`,
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  backdropFilter: 'blur(14px) saturate(130%)',
  WebkitBackdropFilter: 'blur(14px) saturate(130%)',
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.045)}`,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const GlassField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: alpha(theme.palette.common.white, 0.48),
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.18s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.common.white, 0.80),
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.30),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.common.white, 0.68),
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.52),
      }
    }
  }
}));

const EditSectionModal = ({ open, onClose, section, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    about: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setLoading(false);
      setError('');
      setSuccess(false);

      if (section) {
        setFormData({
          title: section.title || '',
          description: section.description || '',
          about: section.about || ''
        });
      }
      setErrors({});
    }
  }, [open, section]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Введите название секции';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const updateData = {
        title: formData.title,
        description: formData.description,
        about: formData.about
      };

      const response = await sectionsAPI.updateSection(section.id, updateData);

      setSuccess(true);

      setTimeout(() => {
        onClose();
        if (onSave) onSave(response);
      }, 1500);
    } catch (err) {
      console.error('❌ Ошибка сохранения секции:', err);
      setError(err.response?.data?.error || 'Не удалось сохранить изменения');
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleFormCancel} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 42, height: 42, flexShrink: 0, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: alpha('#fff', 0.48),
            border: `1px solid ${alpha('#fff', 0.78)}`,
            color: 'primary.main'
          }}>
            <MenuBookIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight="700" noWrap sx={{ color: 'primary.main', lineHeight: 1.15 }}>
              Редактирование секции
            </Typography>
            {section?.title && (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.55 }}>
                {section.title}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={handleFormCancel}
          sx={{
            width: 40, height: 40, flexShrink: 0,
            color: 'text.secondary',
            backgroundColor: alpha('#fff', 0.44),
            border: `1px solid ${alpha('#fff', 0.80)}`,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'rgba(76, 175, 80, 0.09)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent
        sx={{
          p: 2.2,
          pt: 0,
          overflow: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#000', 0.14),
            borderRadius: 999,
            '&:hover': { background: alpha('#000', 0.22) }
          }
        }}
      >
        <Box sx={{ mt: 2 }}>
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 1.6, borderRadius: 2.5,
                backgroundColor: alpha('#fff', 0.58),
                border: `1px solid ${alpha('#4caf50', 0.16)}`
              }}
            >
              Изменения успешно сохранены!
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{
                mb: 1.6, borderRadius: 2.5,
                backgroundColor: alpha('#fff', 0.58),
                border: `1px solid ${alpha('#f44336', 0.16)}`
              }}
            >
              {error}
            </Alert>
          )}

          <GlassSection sx={{ mb: 1.6 }}>
            <SectionLabel>Основная информация</SectionLabel>
            <GlassField
              fullWidth
              label="Название секции *"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              error={!!errors.title}
              helperText={errors.title}
            />
          </GlassSection>

          <GlassSection sx={{ mb: 1.6 }}>
            <SectionLabel>Краткое описание · для шапки</SectionLabel>
            <GlassField
              fullWidth
              label="Краткое описание"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder="Краткое описание, которое будет отображаться в шапке секции"
            />
          </GlassSection>

          <GlassSection sx={{ mb: 1 }}>
            <SectionLabel>Подробная информация · блок «О секции»</SectionLabel>
            <GlassField
              fullWidth
              label="Подробная информация"
              name="about"
              value={formData.about}
              onChange={handleInputChange}
              multiline
              rows={8}
              placeholder="Введите подробную информацию о секции, её тематике, направлениях работы, требованиях к докладам и т.д."
            />
          </GlassSection>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 2.2, py: 1.5, gap: 1,
        borderTop: `1px solid ${alpha('#fff', 0.72)}`,
        backgroundColor: alpha('#fff', 0.20),
        flexShrink: 0
      }}>
        <Button
          onClick={handleFormCancel}
          startIcon={<CancelIcon />}
          sx={{ borderRadius: 999, px: 2.5, textTransform: 'none', color: 'text.secondary' }}
        >
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={19} /> : <SaveIcon />}
          sx={{
            borderRadius: 999, px: 2.8, textTransform: 'none',
            boxShadow: `0 7px 20px ${alpha('#000', 0.12)}`
          }}
        >
          {loading ? 'Сохранение...' : 'Сохранить изменения'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditSectionModal;
